import { ToggleGroup, Options } from '@instructure/ui';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import {
  Badge,
  Tag,
  IconButton,
  Menu,
  IconExternalLinkLine,
  IconSettingsLine,
  Flex,
  View,
  Text,
} from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * Reconnect Component
 * 
 * Displays a collapsible list of sessions that are waiting due to reconnection issues.
 * Shows the student's name and provides options to start check-in or transfer the session.
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.expanded=true] - Whether the toggle group is expanded by default
 * @param {Function} props.onMenuDismiss - Callback to close the parent popover
 * @returns {JSX.Element} The reconnect sessions component
 */
const Reconnect = ({ expanded = true, onMenuDismiss }) => {
  const openDrawer = useAppStore((state) => state.openDrawer);
  const reconnect = useSessionStore((state) => state.reconnect);
  const setTransferSessionSid = useSessionStore((state) => state.setTransferSessionSid);

  /**
   * Renders the options menu for a session
   * @param {string} sessionSid - The session identifier
   * @returns {JSX.Element} Menu with check-in and transfer options
   */
  const renderOptionsMenu = (sessionSid) => {
    return (
      <Menu
        placement="bottom end"
        shouldContainFocus
        shouldReturnFocus
        mountNode={() => document.body}
        onDismiss={onMenuDismiss}
        trigger={
          <IconButton
            withBackground={false}
            withBorder={false}
            color="primary"
            size="small"
            screenReaderLabel="Session Options"
            margin="none"
            padding="none"
          >
            <IconSettingsLine />
          </IconButton>
        }
      >
        {/* Start Check-in option */}
        <Menu.Item onSelect={() => openDrawer('focus', sessionSid)}>
          <Flex alignItems="center">
            <Flex.Item margin="0 xx-small 0 0">
              <IconExternalLinkLine size="x-small" />
            </Flex.Item>
            <Flex.Item>
              <Text>Start Check-in</Text>
            </Flex.Item>
          </Flex>
        </Menu.Item>

        <Menu.Separator />

        {/* Transfer option */}
        <Menu.Item onSelect={() => setTransferSessionSid(sessionSid)}>
          <Flex alignItems="center">
            <Flex.Item margin="0 xx-small 0 0">
              <IconExternalLinkLine size="x-small" />
            </Flex.Item>
            <Flex.Item>
              <Text>Transfer</Text>
            </Flex.Item>
          </Flex>
        </Menu.Item>
      </Menu>
    );
  };

  const reconnectCount = Object.keys(reconnect).length;

  return (
    <ToggleGroup
      toggleLabel="Sessions that are waiting due to reconnection"
      padding="0 0 xxx-small 0"
      defaultExpanded={expanded}
      summary={
        <Flex justifyItems="space-between" margin="none small none none">
          <Flex.Item>Reconnected Sessions <InfoIcon margin="0 0 xxx-small xx-small" color="secondary" tip="This includes sessions that had dropped out of the room and are reconnecting." size="xx-small" /></Flex.Item>
          <Flex.Item>
            <Badge
              count={reconnectCount}
              variant="danger"
              standalone
            />
          </Flex.Item>
        </Flex>
      }
    >
      {reconnectCount > 0 ? (
        <Options>
          {Object.entries(reconnect).map(([sessionSid, data]) => (
            <Options.Item key={`session_reconnect_${sessionSid}`}>
              <Flex alignItems="center">
                {/* Reconnection indicator tag */}
                <Flex.Item width="3rem">
                  <Tag size="small" text="21" />
                </Flex.Item>

                {/* Student name */}
                <Flex.Item textAlign="start" shouldGrow>
                  {data.firstName} {data.lastName}
                </Flex.Item>

                {/* Options menu */}
                <Flex.Item width="2rem" textAlign="end">
                  {renderOptionsMenu(sessionSid)}
                </Flex.Item>
              </Flex>
            </Options.Item>
          ))}
        </Options>
      ) : (
        <View as="div" padding="small" textAlign="center">
          <Text>No sessions waiting due to reconnection</Text>
        </View>
      )}
    </ToggleGroup>
  );
};

export default Reconnect;
